import { useMemo } from 'react';
import { bucketSearchQuery } from '../charts/drilldown';
import { TrendChart } from '../charts/TrendChart';
import { navigate } from '../router';
import { EmptyState, ErrorText, Loading, Section } from '../ui';
import { TRAFFIC_ERROR_SERIES, trafficSeries } from './traffic';
import { useTraffic } from './useTraffic';

/**
 * 트레이스만 서비스의 초당 요청·오류·응답시간 (34회차).
 *
 * 스냅샷이 오는 서비스의 같은 자리는 `RateTrendSection`이다. 그쪽은 화면이 1초 값을 모으고
 * 이쪽은 서버가 접은 것을 받는다. 그림과 누르면 가는 곳은 둘이 같다.
 */
export function TrafficSection({ service, now }: { service: string; now?: number }) {
  const query = useTraffic(service);
  const result = query.data;
  // 응답이 바뀔 때만 계열을 만든다 (44회차). 이 화면은 실시간 프레임마다 다시 그려지는데, 계열을
  // 새로 만들면 추이 그래프가 그때마다 캔버스를 다시 그린다
  const chart = useMemo(() => (result === undefined ? undefined : trafficSeries(result)), [result]);

  return (
    <Section
      boxed
      title="최근 30분 초당 요청·오류"
      note={[
        '끝난 요청을 관제 서버가 1분 단위로 모은 초당 평균. 실시간 연결은 뺀다.',
        '이 앱은 1초 보고를 보내지 않으므로 이 값이 실시간 지표를 대신한다',
      ]}
    >
      <div className="rounded-xl border border-hair bg-panel p-3">
        {query.loading && result === undefined ? <Loading /> : null}
        {query.error ? <ErrorText>{query.error}</ErrorText> : null}
        {chart !== undefined && result !== undefined ? (
          result.rows.length === 0 ? (
            <EmptyState
              title="최근 30분에 요청이 없다"
              hint="이 앱이 조용한 것이지 연결이 끊긴 것은 아니다"
            />
          ) : (
            <TrendChart
              buckets={chart.buckets}
              bucketMs={result.bucketMs}
              series={chart.series}
              label="최근 30분 초당 요청·오류와 응답시간. 1분 단위"
              onPick={(pick) => {
                navigate({
                  name: 'requests',
                  query: bucketSearchQuery(
                    pick.bucketStart,
                    result.bucketMs,
                    { errorsOnly: pick.series === TRAFFIC_ERROR_SERIES, service },
                    // 마지막 구간은 아직 흐르는 중이다. 지금까지로 자른다
                    { to: now ?? Date.now() },
                  ),
                });
              }}
            />
          )
        ) : null}
      </div>
    </Section>
  );
}
